import React, { useMemo, useState } from "react";
import { BiSitemap } from "react-icons/bi";
import {
  buildProductImageUrl,
  getFallbackImageUrl,
  getProductImageUrl,
} from "../../../utils/productImage";

const ParentSectionCard = ({
  parentSection,
  sectionLookup,
  currentSectionId,
}) => {
  const [activeChoiceId, setActiveChoiceId] = useState(null);

  const choiceItems = useMemo(() => {
    if (!parentSection) {
      return [];
    }
    return Array.isArray(parentSection.choice_items)
      ? parentSection.choice_items
      : [];
  }, [parentSection]);

  const childSectionsByChoice = useMemo(() => {
    if (!choiceItems.length) {
      return {};
    }
    return choiceItems.reduce((acc, choice) => {
      if (!Array.isArray(choice.children) || !choice.children.length) {
        acc[choice.id] = [];
        return acc;
      }
      acc[choice.id] = choice.children
        .map((childId) => sectionLookup[childId])
        .filter(Boolean);
      return acc;
    }, {});
  }, [choiceItems, sectionLookup]);

  const toggleChoice = (choiceId) => {
    setActiveChoiceId((prev) => (prev === choiceId ? null : choiceId));
  };

  if (!parentSection) {
    return null;
  }

  const renderChildSectionPreview = (section, index) => {
    if (!section) {
      return null;
    }

    const hasNested = Array.isArray(section.nested_sections) && section.nested_sections.length > 0;
    const hasChoices =
      (Array.isArray(section.choice_items) && section.choice_items.length > 0) ||
      (Array.isArray(section.choices) && section.choices.length > 0);
    const productCount = Array.isArray(section.products) ? section.products.length : 0;

    return (
      <div
        key={`${section.id}-${index}`}
        className={`parent-child-card ${
          section.id === currentSectionId ? "parent-child-card-active" : ""
        }`}
      >
        <div className="parent-child-card-header">
          <div>
            <span className="parent-child-title">
              {section.name || section.code || "Sans nom"}
            </span>
            {section.code && (
              <small className="text-muted ms-2">({section.code})</small>
            )}
          </div>
          {section.section_type && (
            <span className="badge bg-light text-uppercase text-muted parent-child-badge">
              {section.section_type}
            </span>
          )}
        </div>
        {section.description && (
          <p className="parent-child-description text-muted">
            {section.description}
          </p>
        )}
        <div className="parent-child-meta">
          {productCount > 0 && (
            <span>{productCount} produit(s)</span>
          )}
          {hasChoices && <span>Choix imbriqués</span>}
          {hasNested && <span>{section.nested_sections.length} nested section(s)</span>}
        </div>
        <div className="parent-child-actions">
          <a
            href={`#section-${section.id}`}
            className="parent-child-link btn btn-link p-0"
          >
            Ouvrir la section
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="parent-section-card">
      <div className="d-flex align-items-start mb-3">
        <BiSitemap
          size={22}
          className="me-2 text-primary flex-shrink-0 mt-1"
        />
        <div>
          <p className="mb-1 fw-semibold">
            Section parente : {parentSection.name || "Sans nom"}
          </p>
          <small className="text-muted d-block">
            Code : {parentSection.code || "—"} | Type :{" "}
            {parentSection.section_type || "—"}
          </small>
          {parentSection.description && (
            <small className="text-muted d-block mt-1">
              {parentSection.description}
            </small>
          )}
        </div>
      </div>

      {choiceItems.length > 0 && (
        <>
          <p className="text-muted fw-semibold small mb-3">
            Choix disponibles ({choiceItems.length})
          </p>
          <div className="choice-items-grid parent-choice-grid">
            {choiceItems.map((choice) => {
              const childSections = childSectionsByChoice[choice.id] || [];
              const isCurrentChoice = childSections.some(
                (child) => child.id === currentSectionId
              );
              const isActiveChoice = activeChoiceId === choice.id;

              const linkedProductImage =
                getProductImageUrl(choice.linked_product) ||
                getProductImageUrl(choice.product_ref);
              const rawChoiceImage =
                buildProductImageUrl(choice.image) ||
                buildProductImageUrl(choice.profile_image);
              const finalChoiceImage =
                linkedProductImage || rawChoiceImage || getFallbackImageUrl();

              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={() => toggleChoice(choice.id)}
                  className={`choice-card parent-choice-card ${
                    isCurrentChoice ? "parent-choice-highlight" : ""
                  } ${isActiveChoice ? "selected" : ""}`}
                >
                  <div className="choice-card-header">
                    <span className="choice-card-label">{choice.label}</span>
                    {isCurrentChoice && (
                      <span className="choice-card-badge">Section active</span>
                    )}
                  </div>
                  {finalChoiceImage && (
                    <div className="choice-card-image">
                      <img src={finalChoiceImage} alt={choice.label} />
                    </div>
                  )}
                  {choice.help_text && (
                    <p className="choice-card-help">{choice.help_text}</p>
                  )}
                  <div className="choice-card-meta">
                    {choice.code && <span>Code : {choice.code}</span>}
                    {choice.add_quantities && (
                      <span className="choice-card-quantity">
                        Quantité ajustable
                      </span>
                    )}
                  </div>
                  {isActiveChoice && (
                    <div className="parent-choice-children mt-3">
                      {childSections.length > 0 ? (
                        <>
                          <span className="text-muted small d-block mb-1">
                            Sous-sections associées ({childSections.length})
                          </span>
                          <div className="parent-child-grid">
                            {childSections.map((childSection, idx) =>
                              renderChildSectionPreview(childSection, idx)
                            )}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted small">
                          Aucun sous-choix associé à cette option.
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ParentSectionCard;

